import React from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, Option, Field } from '@fluentui/react-components';
import type { AccessCodeResult, LawCodeResult, AccessGroupResult } from '../../../../services/api/dataServiceAccessReview';
import { useAccessReviewStyles } from '../styles';

interface AccessRightsFormProps {
    // Access Code
    accessCodes: AccessCodeResult[];
    selectedAccessCode: string;
    onAccessCodeChange: (value: string) => void;
    isLoadingAccessCodes: boolean;

    // Law Code (Paragraph)
    lawCodes: LawCodeResult[];
    selectedParagraph: string;
    onParagraphChange: (value: string) => void;
    isLoadingLawCodes: boolean;

    // Access Group
    accessGroups: AccessGroupResult[];
    selectedAccessGroup: string;
    onAccessGroupChange: (value: string) => void;
    isLoadingAccessGroups: boolean;

    // Visibility / mandatory flags derived from ArchiveHasAccessCode / ArchiveHasAccessGroup
    showAccessCode?: boolean;
    isAccessCodeRequired?: boolean;
    showAccessGroup?: boolean;
    isAccessGroupRequired?: boolean;
}

export const AccessRightsForm: React.FC<AccessRightsFormProps> = ({
    accessCodes,
    selectedAccessCode,
    onAccessCodeChange,
    isLoadingAccessCodes,
    lawCodes,
    selectedParagraph,
    onParagraphChange,
    isLoadingLawCodes,
    accessGroups,
    selectedAccessGroup,
    onAccessGroupChange,
    isLoadingAccessGroups,
    showAccessCode = true,
    isAccessCodeRequired = true,
    showAccessGroup = true,
    isAccessGroupRequired = true,
}) => {
    const { t } = useTranslation();
    const styles = useAccessReviewStyles();

    // Determine if paragraph is required based on selected access code
    const selectedAccessCodeObj = accessCodes.find(ac => ac.Recno.toString() === selectedAccessCode);
    const isParagraphRequired = selectedAccessCodeObj?.IsParagraphRequired ?? false;

    // Truncate paragraph text with ellipsis
    const truncateParagraph = (text: string, maxLength: number = 80): string => {
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    };

    return (
        <div className={styles.accessReviewFlexColumnWithGap}>
            {showAccessCode && (
                <Field label={t('Access Code')} required={isAccessCodeRequired}>
                    <Dropdown
                        placeholder={t('Select access code')}
                        value={accessCodes.find(ac => ac.Recno.toString() === selectedAccessCode)?.Description || ''}
                        selectedOptions={selectedAccessCode ? [selectedAccessCode] : []}
                        onOptionSelect={(_, data) => onAccessCodeChange(data.optionValue as string)}
                        disabled={isLoadingAccessCodes}
                    >
                        {accessCodes.map((code) => (
                            <Option key={code.Recno} value={code.Recno.toString()}>
                                {code.Description}
                            </Option>
                        ))}
                    </Dropdown>
                </Field>
            )}

            {showAccessCode && (
                <Field label={t('Access Paragraph')} required={isParagraphRequired}>
                    <Dropdown
                        className={styles.accessReviewParagraphDropdown}
                        placeholder={isParagraphRequired ? t('Select paragraph') : t('Select paragraph (optional)')}
                        value={truncateParagraph(lawCodes.find(lc => lc.Code === selectedParagraph)?.Description || '')}
                        selectedOptions={selectedParagraph ? [selectedParagraph] : []}
                        onOptionSelect={(_, data) => onParagraphChange(data.optionValue as string)}
                        disabled={!selectedAccessCode || isLoadingLawCodes}
                    >
                        {lawCodes.map((code) => (
                            <Option
                                key={code.Code}
                                value={code.Code}
                                title={code.Description}
                                className={styles.accessReviewParagraphOption}
                            >
                                {truncateParagraph(code.Description)}
                            </Option>
                        ))}
                    </Dropdown>
                </Field>
            )}

            {showAccessGroup && (
                <Field label={t('Access Group')} required={isAccessGroupRequired}>
                    <Dropdown
                        placeholder={t('Select access group')}
                        value={accessGroups.find(ag => ag.Recno.toString() === selectedAccessGroup)?.Name || ''}
                        selectedOptions={selectedAccessGroup ? [selectedAccessGroup] : []}
                        onOptionSelect={(_, data) => onAccessGroupChange(data.optionValue ?? '')}
                        disabled={!selectedAccessCode || isLoadingAccessGroups}
                    >
                        {accessGroups.map((group) => (
                            <Option key={group.Recno} value={group.Recno.toString()}>
                                {group.Name}
                            </Option>
                        ))}
                    </Dropdown>
                </Field>
            )}
        </div>
    );
};
